import React, { createContext, useState, useContext, useEffect } from 'react';
import { translateText } from '../services/translationService';
import { translatePageContent, initializeTranslation } from '../utils/translationUtils';

// Create the context
const TranslationContext = createContext();

/**
 * Translation Provider Component
 * Manages the current language and provides translation functions
 */
export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Function to change the language
  const changeLanguage = (langCode) => {
    if (langCode === currentLanguage) return;
    
    // Store the previous language for reference
    const previousLanguage = currentLanguage;
    
    // Clear translations when changing languages
    setTranslations({});
    
    // Set the new language
    setCurrentLanguage(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    
    // Translate the page content
    translatePageContent(langCode, previousLanguage);
  };
  
  // Function to translate text
  const translate = async (text, key) => {
    // If language is English, return the original text
    if (currentLanguage === 'en' || !text) {
      return text;
    }
    
    // If we already have a translation for this key, use it
    if (key && translations[key]) {
      return translations[key];
    }
    
    try {
      setIsTranslating(true);
      
      // Translate the text
      const translatedText = await translateText(text, currentLanguage);
      
      // If we have a key, store the translation
      if (key) {
        setTranslations(prev => ({
          ...prev,
          [key]: translatedText
        }));
      }
      
      return translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      return text; // Return original text on error
    } finally {
      setIsTranslating(false);
    }
  };
  
  // Initialize translation system
  useEffect(() => {
    initializeTranslation();
  }, []);
  
  // Load preferred language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
      
      // If the saved language is not English, translate the page
      if (savedLanguage !== 'en') {
        translatePageContent(savedLanguage);
      }
    }
  }, []);
  
  // Context value
  const contextValue = {
    currentLanguage,
    changeLanguage,
    translate,
    isTranslating,
    translations
  };
  
  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Custom hook to use the translation context
 * @returns {Object} The translation context
 */
export const useCustomTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useCustomTranslation must be used within a TranslationProvider');
  }
  return context;
};
